/*
 * Normalizes text contrast inside blog/article HTML content (.rte and
 * [data-contrast-fix] scopes). Merchant-pasted HTML often carries inline
 * colors and backgrounds that clash with the theme's light/dark modes.
 *
 * For every element we composite its effective background (walking up
 * through inline/CSS backgrounds to the page background), then:
 *   - saturated colors (accents like the brand red) are left untouched
 *   - neutral/greyish text that fails WCAG-ish contrast against its real
 *     background is forced to the theme's near-white (dark bg) or
 *     near-black (light bg), overriding any inline styling
 *
 * Re-runs whenever the light/dark toggle flips data-theme on <html>.
 */
(function () {
  'use strict';

  var SCOPE_SELECTOR = '.rte, [data-contrast-fix]';
  var SKIP_TAGS = {
    SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, IMG: 1, PICTURE: 1,
    VIDEO: 1, AUDIO: 1, IFRAME: 1, SVG: 1, PATH: 1, BR: 1, HR: 1,
    CANVAS: 1, INPUT: 1, SELECT: 1, TEXTAREA: 1, BUTTON: 1
  };
  var DARK_TEXT = '#171311';
  var LIGHT_TEXT = '#f5f2eb';
  var MIN_CONTRAST = 4.5;
  var ACCENT_CHROMA = 40; /* 0-255; above this a color is a deliberate accent */

  function parseColor(str) {
    if (!str) return null;
    var m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+%?))?\s*\)/.exec(str);
    if (!m) return null;
    var a = 1;
    if (m[4] !== undefined) {
      a = m[4].indexOf('%') > -1 ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    }
    return [+m[1], +m[2], +m[3], a];
  }

  function luminance(rgb) {
    var f = function (v) {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * f(rgb[0]) + 0.7152 * f(rgb[1]) + 0.0722 * f(rgb[2]);
  }

  function contrast(a, b) {
    var l1 = luminance(a);
    var l2 = luminance(b);
    var hi = Math.max(l1, l2);
    var lo = Math.min(l1, l2);
    return (hi + 0.05) / (lo + 0.05);
  }

  function blend(src, dst) {
    var a = src[3];
    return [
      src[0] * a + dst[0] * (1 - a),
      src[1] * a + dst[1] * (1 - a),
      src[2] * a + dst[2] * (1 - a),
      1
    ];
  }

  /* Composite the element's real background: gather every painted
     background-color from the element up to <html>, then blend them over
     the page background from the outside in. */
  function effectiveBackground(el, pageBg) {
    var layers = [];
    var node = el;
    while (node && node !== document.documentElement) {
      var bg = parseColor(getComputedStyle(node).backgroundColor);
      if (bg && bg[3] > 0) {
        layers.push(bg);
        if (bg[3] >= 0.99) break;
      }
      node = node.parentElement;
    }
    var result = pageBg;
    for (var i = layers.length - 1; i >= 0; i--) {
      result = blend(layers[i], result);
    }
    return result;
  }

  function restore(el) {
    if (!el.hasAttribute('data-cc-on')) return;
    if (el.hasAttribute('data-cc-prev')) {
      el.style.setProperty('color', el.getAttribute('data-cc-prev'), el.getAttribute('data-cc-prio') || '');
      el.removeAttribute('data-cc-prev');
      el.removeAttribute('data-cc-prio');
    } else {
      el.style.removeProperty('color');
    }
    el.removeAttribute('data-cc-on');
  }

  function hasDirectText(el) {
    for (var n = el.firstChild; n; n = n.nextSibling) {
      if (n.nodeType === 3 && /\S/.test(n.nodeValue)) return true;
    }
    return false;
  }

  function processElement(el, pageBg) {
    restore(el);
    if (SKIP_TAGS[el.tagName]) return;
    if (!hasDirectText(el)) return;

    var color = parseColor(getComputedStyle(el).color);
    if (!color) return;

    /* Deliberate accent colors (brand red, colored code text, links) stay. */
    var chroma = Math.max(color[0], color[1], color[2]) - Math.min(color[0], color[1], color[2]);
    if (chroma > ACCENT_CHROMA) return;

    var bg = effectiveBackground(el, pageBg);
    var flat = color[3] < 1 ? blend(color, bg) : color;
    if (contrast(flat, bg) >= MIN_CONTRAST) return;

    var desired = luminance(bg) < 0.4 ? LIGHT_TEXT : DARK_TEXT;
    var prev = el.style.getPropertyValue('color');
    if (prev) {
      el.setAttribute('data-cc-prev', prev);
      el.setAttribute('data-cc-prio', el.style.getPropertyPriority('color'));
    }
    el.setAttribute('data-cc-on', '1');
    el.style.setProperty('color', desired, 'important');
  }

  function run() {
    var scopes = document.querySelectorAll(SCOPE_SELECTOR);
    if (!scopes.length) return;
    var pageBg = parseColor(getComputedStyle(document.body).backgroundColor);
    if (!pageBg || pageBg[3] < 1) {
      pageBg = document.documentElement.getAttribute('data-theme') === 'light'
        ? [244, 241, 238, 1]
        : [5, 5, 5, 1];
    }
    for (var s = 0; s < scopes.length; s++) {
      var scope = scopes[s];
      processElement(scope, pageBg);
      var all = scope.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) {
        processElement(all[i], pageBg);
      }
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      run();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  /* Re-evaluate when the theme toggle flips light/dark. Theme colors
     transition over ~420ms, so run once right away for quick feedback and
     again after the transition settles to read the final colors. */
  new MutationObserver(function () {
    schedule();
    setTimeout(schedule, 550);
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme']
  });
})();
